import { ArrayMethodPlateRoleId } from "enums/ArrayMethodPlateRoleIds";
import { DefaultWorklistASAMs, Methods } from "enums/Methods";
import {
  extractPlateCode,
  getRowAndColCountByLabwareType,
  getSourcePlateInfo,
  getValueInDetailsString,
} from "features/WorklistTools/shared/WorklistHelpers";
import Twelve24TCWorklistService, {
  GetASAMAndPlateInfoData,
} from "services/Twelve24TCWorklistToolService";
import { DestPlateInfoState, SourcePlateInfoState } from "../../state";
import { getSourcePlateInfoState } from "../../state-helpers";
import { parse1224TCMethodSettings } from "./ParseMethodSettings";

/**
 * NOTE: The shared upload dialog parses with papaparse `dynamicTyping`, so at runtime
 * empty cells arrive as null and numeric cells as numbers. Always read cells through
 * `asText` rather than trusting these declared types.
 */
export interface UploadedWorklist {
  task: string;
  details: string;
  aspiratevol: string;
  dispensevol: string;
  transfervol: string;
  sourceplatetype: string;
  sourceplatebarcode: string;
  sourcewellid: string;
  destinationplatetype: string;
  destinationplatebarcode: string;
  destinationwellid: string;
  rownumber: string;
}

const REQUIRED_TASKS = [
  "MethodData",
  "HarvestWells",
  "SourcePlate",
  "Dissociation",
  "Incubation",
  "Spin",
  "PelletResuspension",
  "TopUp",
  "DeadTotalPlate",
];

const asText = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

export const parsePlateInfo = (
  uploadedWorklist: UploadedWorklist[],
  key: keyof UploadedWorklist,
) => {
  return [
    ...new Map(uploadedWorklist.map((rows) => [rows[key], rows])).values(),
  ];
};

const mapWorklistRows = (
  row: UploadedWorklist,
  sourcePlateIndex: number,
  destPlateIndex: number,
) => {
  return {
    sourcePlateIndex: sourcePlateIndex,
    sourceWellId: asText(row.sourcewellid),
    destPlateIndex: destPlateIndex,
    destWellId: asText(row.destinationwellid),
    transferVol: asText(row.transfervol),
  };
};

const validateWorklist = (worklistRows: UploadedWorklist[]) => {
  const missingRequiredTasks = REQUIRED_TASKS.filter(
    (task) => !worklistRows.some((row) => asText(row.task) === task),
  );

  if (missingRequiredTasks.length > 0) {
    throw new Error(
      `Missing required tasks in worklist: ${missingRequiredTasks.join(", ")}`,
    );
  }

  // The MethodData row is written by the server as "MethodID |145; WorklistId|...",
  // so the key can carry trailing whitespace before the pipe
  const methodDataString = asText(
    worklistRows.find((e) => asText(e.task) === "MethodData")?.details,
  );
  const methodId = methodDataString.match(/MethodID\s*\|\s*(\d+)/)?.[1];

  if (methodId !== Methods.Twelve24TC.toString()) {
    throw new Error(
      "This file is not a 1224TC worklist. Please upload a worklist generated by this tool.",
    );
  }
};

export const parseUploadedWorklist = async (
  worklistRows: UploadedWorklist[],
) => {
  validateWorklist(worklistRows);

  // MethodData is in REQUIRED_TASKS, so validation has already proven the row exists
  const methodDataString = asText(
    worklistRows.find((e) => asText(e.task) === "MethodData")?.details,
  );

  const methodReservationId = getValueInDetailsString(
    methodDataString,
    "MethodReservationId",
  );

  // Get data not in worklist
  const { data: asamAndPlateData } =
    await Twelve24TCWorklistService.getASAMAndPlateInfo(methodReservationId);

  const getLabwareType = (
    arrayMethodPlateRoleId: ArrayMethodPlateRoleId,
    labwareTypeCode: string,
  ) =>
    asamAndPlateData.acceptablePlateTypes?.find(
      (plate) =>
        plate.labwareType?.arrayMethodPlateRoleId === arrayMethodPlateRoleId &&
        plate.labwareType?.labwareTypeCode === labwareTypeCode,
    )?.labwareType;

  const harvestWellsRows = worklistRows.filter(
    (e) => asText(e.task) === "HarvestWells",
  );
  const sourcePlateRows = worklistRows.filter(
    (e) => asText(e.task) === "SourcePlate",
  );

  const sourcePlateInfo: SourcePlateInfoState[] = Array.from(
    { length: 8 },
    () => getSourcePlateInfoState(),
  );

  const uniqueSourcePlates = parsePlateInfo(
    sourcePlateRows,
    "sourceplatebarcode",
  );

  const uniqueDestinationPlates = parsePlateInfo(
    harvestWellsRows,
    "destinationplatebarcode",
  );

  if (uniqueSourcePlates.length > sourcePlateInfo.length) {
    throw new Error(
      `This worklist has ${uniqueSourcePlates.length} source plates, but the tool supports at most ${sourcePlateInfo.length}.`,
    );
  }

  for (const [index, row] of uniqueSourcePlates.entries()) {
    const plateBarcode = asText(row.sourceplatebarcode);
    const labwareTypeCode = asText(row.sourceplatetype);
    const wellInfo = await getSourcePlateInfo(0, plateBarcode, false);
    const rowColCount = getRowAndColCountByLabwareType(labwareTypeCode);
    const labwareType = getLabwareType(
      ArrayMethodPlateRoleId.Source,
      labwareTypeCode,
    );
    const discard = getValueInDetailsString(asText(row.details), "Discard", "");

    sourcePlateInfo[index] = {
      plateBarcode: plateBarcode,
      labwareTypeCode: labwareTypeCode,
      operatingVol:
        labwareType?.defaultLabwareVolume ?? rowColCount.operatingVol,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      wellInfo: wellInfo,
      discardSourcePlate: discard === "" ? -1 : parseInt(discard, 10),
    };
  }

  // 1224TC only ever has a single destination plate
  const destBarcode = asText(
    uniqueDestinationPlates[0]?.destinationplatebarcode,
  );
  const destLabwareTypeCode = asText(
    uniqueDestinationPlates[0]?.destinationplatetype,
  );
  const destRowColCount = getRowAndColCountByLabwareType(destLabwareTypeCode);
  const destLabwareType = getLabwareType(
    ArrayMethodPlateRoleId.Destination,
    destLabwareTypeCode,
  );
  const topUpString = asText(
    worklistRows.find((e) => asText(e.task) === "TopUp")?.details,
  );

  const destPlateInfo: DestPlateInfoState = {
    plateBarcode: destBarcode,
    labwareTypeCode: destLabwareTypeCode,
    operatingVol:
      destLabwareType?.defaultLabwareVolume ?? destRowColCount.operatingVol,
    minOperatingVol: destLabwareType?.minLabwareVolume ?? 0,
    startingVol: 0,
    rows: destRowColCount.rows,
    cols: destRowColCount.cols,
    preprocess: false,
    plateCode: extractPlateCode(destBarcode),
    topupVol: parseInt(getValueInDetailsString(topUpString, "Volume", "0"), 10),
  };

  const deadPlateRow = worklistRows.find(
    (row) => asText(row.task) === "DeadTotalPlate",
  );
  const deadPlateBarcode = asText(deadPlateRow?.sourceplatebarcode);
  const deadPlateType = asText(deadPlateRow?.sourceplatetype);

  const harvestWells = harvestWellsRows.map((row) => {
    const sourcePlateIndex = sourcePlateInfo.findIndex(
      (plate) =>
        plate.plateBarcode !== "" &&
        plate.plateBarcode === asText(row.sourceplatebarcode),
    );

    if (sourcePlateIndex === -1) {
      throw new Error(
        `Worklist maps wells from source plate ${asText(
          row.sourceplatebarcode,
        )}, which has no SourcePlate row.`,
      );
    }

    return mapWorklistRows(row, sourcePlateIndex, 0);
  });

  const methodSettings = parseMethodSettingsFromWorklist(
    worklistRows,
    asamAndPlateData,
  );

  const parsedWorklistInfo = {
    harvestWells,
    sourcePlateInfo,
    destPlateInfo,
    deadPlateBarcode,
    deadPlateType,
    methodSettings,
  };

  return parsedWorklistInfo;
};

const parseMethodSettingsFromWorklist = (
  uploadedWorklist: UploadedWorklist[],
  asamAndPlateData: GetASAMAndPlateInfoData,
) =>
  parse1224TCMethodSettings(
    uploadedWorklist.map((row) => ({
      task: asText(row.task),
      details: asText(row.details),
      transferVol: asText(row.transfervol),
    })),
    {
      incomingTimeUnit: "seconds",
      selectedSystem: asamAndPlateData.system ?? 0,
      arraySystemAutomationMethodId:
        asamAndPlateData.arraySystemAutomationMethodId ??
        DefaultWorklistASAMs.Twelve24TC,
    },
  );
